import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ClipTransport, PLAYBACK_RATES } from "./ClipTransport";
import { LiveCaption } from "./LiveCaption";
import { MultiPlatformPlayer } from "./MultiPlatformPlayer";
import type { MultiPlatformPlayerRef } from "./MultiPlatformPlayer";

import { useCaptionWindow } from "@/hooks/use-caption-window";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatTimestamp, supportsTimeSync } from "@/lib/video-types";
import type { VideoCaptionHit } from "@/lib/video-types";
import { splitCaptionByQuery } from "@/lib/caption-highlight";
import { typeBodyClass, typeNumericClass } from "@/lib/typography";

export interface CaptionClipDeskProps {
    /** The clips the desk is stepping through — a search page or a topic's set. */
    hits: VideoCaptionHit[];
    /** What the reader searched, so the caption line highlights the match. */
    query: string;
    /** The clip currently loaded. `null` renders nothing. */
    activeClip: VideoCaptionHit | null;
    /** Step to a clip. The caller owns selection and any scrolling. */
    onSelect: (hit: VideoCaptionHit) => void;
}

/**
 * The player half of "in context": one clip, its transport, and the caption
 * line that follows playback.
 *
 * Extracted from `VideoInContext` unchanged so a topic can open the same desk
 * inline — the point of the feature is that a hit is a *moment*, and a second
 * copy of this logic would drift away from the page that defines it.
 *
 * Deliberately chrome-free: the caller supplies the card. On the search page
 * that is `slateCardClass`; in a word dialog it is the dialog surface; in the
 * topic rail it is the vocabulary panel.
 */
export function CaptionClipDesk({
    hits,
    query,
    activeClip,
    onSelect,
}: CaptionClipDeskProps) {
    const { t } = useTranslation();

    // Playback control state. `loop` restarts the clip at its end; `rate` is the
    // shadowing ladder. Both reset with the clip, because "repeat the last line
    // at 0.5x" is not an instruction for the next one.
    const playerRef = useRef<MultiPlatformPlayerRef | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loop, setLoop] = useState(false);
    const [rate, setRate] = useState<number>(1);

    /**
     * Playback position, kept in *whole seconds* on purpose. The player reports
     * every 50 ms; storing that verbatim re-renders twenty times a second for a
     * caption that only changes once per line.
     */
    const [captionTime, setCaptionTime] = useState(0);

    /** Position of the open clip in the visible list, or -1 when it is not there. */
    const activeIndex = useMemo(() => {
        if (!activeClip) return -1;
        return hits.findIndex(
            (hit) =>
                hit.videoRowId === activeClip.videoRowId &&
                hit.source === activeClip.source &&
                hit.start === activeClip.start,
        );
    }, [hits, activeClip]);

    const hasPrevious = activeIndex > 0;
    const hasNext = activeIndex >= 0 && activeIndex < hits.length - 1;

    /**
     * The clip that gets warmed while this one plays.
     *
     * Only YouTube can be warmed: the other platforms are plain embeds with no
     * player API, so there is nothing to start, buffer or promote. They are
     * loaded when they are opened, as before.
     */
    const warmingClip = useMemo(() => {
        if (!hasNext) return null;
        const next = hits[activeIndex + 1];
        return next && supportsTimeSync(next.platform) ? next : null;
    }, [hasNext, hits, activeIndex]);

    /**
     * Two player slots, alternating: the clip on screen takes the slot for the
     * parity of its position, so "next" always lands in the slot that has been
     * warming it and is *promoted* rather than rebuilt. The slot left behind is
     * handed the clip after that and warms it off-screen.
     */
    const activeSlot = activeClip ? Math.abs(activeIndex) % 2 : 0;
    const playerSlots = useMemo(() => {
        const keys = ["a", "b"] as const;
        return keys.map((key, index) => {
            const isActive = activeClip != null && index === activeSlot;
            return {
                key,
                isActive,
                clip: isActive ? activeClip : index === activeSlot ? null : warmingClip,
            };
        });
    }, [activeClip, activeSlot, warmingClip]);

    const goToClip = useCallback(
        (offset: number) => {
            const next = hits[activeIndex + offset];
            if (next) onSelect(next);
        },
        [hits, activeIndex, onSelect],
    );

    const { lines, activeIndex: activeCaptionIndex } = useCaptionWindow({
        source: activeClip?.source ?? "library",
        videoRowId: activeClip?.videoRowId ?? "",
        at: captionTime,
        enabled: Boolean(activeClip),
    });

    const seekBy = useCallback(
        (delta: number) => {
            if (!activeClip) return;
            const from = playerRef.current?.getCurrentTime() || activeClip.start;
            playerRef.current?.seekTo(Math.max(0, from + delta));
        },
        [activeClip],
    );

    const replayClip = useCallback(() => {
        if (!activeClip) return;
        playerRef.current?.seekTo(activeClip.start);
        playerRef.current?.play();
    }, [activeClip]);

    /**
     * Loop without a watcher loop: the player only reports time while it is
     * playing, so the seek fires once per pass. The half-second guard keeps a
     * seek that lands a fraction before `end` from immediately re-triggering.
     */
    const handleTimeUpdate = useCallback(
        (time: number) => {
            const whole = Math.floor(time);
            // Same value in, same value out — React bails out of the render.
            setCaptionTime((previous) => (previous === whole ? previous : whole));

            if (!loop || !activeClip) return;
            if (time >= activeClip.end - 0.25) {
                playerRef.current?.seekTo(activeClip.start);
            }
        },
        [loop, activeClip],
    );

    // Rate survives a clip change (you are still in "slow" mode) but is re-applied
    // because switching videos builds a new player.
    useEffect(() => {
        playerRef.current?.setPlaybackRate(rate);
    }, [rate, activeClip]);

    const cycleRate = useCallback(() => {
        setRate((current) => {
            const index = PLAYBACK_RATES.indexOf(current as (typeof PLAYBACK_RATES)[number]);
            return PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length];
        });
    }, []);

    // Captions belong to the clip that is playing, not the previous one.
    useEffect(() => {
        setCaptionTime(activeClip?.start ?? 0);
    }, [activeClip]);

    if (!activeClip) return null;

    /**
     * Only the curated library has a lesson page. An imported clip is a segment
     * of a channel video this app never published, so everything that points at
     * `/videos/:id` has to be conditional — the route would 404.
     */
    const isLibraryClip = activeClip.source === "library";

    return (
        <div>
            <div className="relative">
                {playerSlots.map((slot) =>
                    slot.clip ? (
                        <div
                            key={slot.key}
                            className={
                                slot.isActive ? undefined : "pointer-events-none absolute inset-0 opacity-0"
                            }
                            aria-hidden={slot.isActive ? undefined : true}
                        >
                            <MultiPlatformPlayer
                                ref={slot.isActive ? playerRef : null}
                                videoId={slot.clip.videoId}
                                platform={slot.clip.platform}
                                originalUrl={slot.clip.videoUrl}
                                startTime={slot.clip.start}
                                active={slot.isActive}
                                onTimeUpdate={handleTimeUpdate}
                                onStateChange={setIsPlaying}
                                className="rounded-none"
                            />
                        </div>
                    ) : null,
                )}
            </div>

            <ClipTransport
                canControl={supportsTimeSync(activeClip.platform)}
                isPlaying={isPlaying}
                onTogglePlay={() => playerRef.current?.toggle()}
                onSeekBy={seekBy}
                onReplay={replayClip}
                onPrevious={() => goToClip(-1)}
                onNext={() => goToClip(1)}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                loop={loop}
                onToggleLoop={() => setLoop((value) => !value)}
                rate={rate}
                onCycleRate={cycleRate}
            >
                <LiveCaption
                    lines={lines}
                    activeIndex={activeCaptionIndex}
                    query={query}
                />

            </ClipTransport>

            {/* The caption line is shown in full and centred, so a long line
                makes the desk taller. That is this column's problem, not the
                modal's: it scrolls (`sm:overflow-y-auto`) while the rail keeps
                its own scroll beside it. */}
            <div className="space-y-2 p-4">
                <p className={cn(typeBodyClass, "text-balance text-center")}>
                    {splitCaptionByQuery(activeClip.text, query).map((segment, index) =>
                        segment.match ? (
                            <mark
                                key={index}
                                className="rounded-[3px] bg-amber-200/70 px-0.5 text-foreground dark:bg-amber-400/25 dark:text-amber-50"
                            >
                                {segment.text}
                            </mark>
                        ) : (
                            <span key={index}>{segment.text}</span>
                        ),
                    )}
                </p>

                <div className="flex flex-wrap items-center justify-between gap-2">
                    {/* `min-w-0` on the row and `whitespace-nowrap` on the
                        timestamp: in the topic rail this row is 360px wide, and
                        without them "0:53–0:56" broke across two lines while the
                        title refused to give up any width. */}
                    <p className="flex min-w-0 items-center gap-2">
                        <span
                            className={cn(
                                typeNumericClass,
                                "shrink-0 whitespace-nowrap text-xs text-muted-foreground",
                            )}
                        >
                            {formatTimestamp(activeClip.start)}–{formatTimestamp(activeClip.end)}
                        </span>
                        <span className="shrink-0 text-muted-foreground/50" aria-hidden="true">
                            ·
                        </span>
                        {/* Only a library row has a lesson page. Imported clips
                            live in the channel corpus and have no `/videos/:id`
                            route, so their title is plain text instead of a link
                            that would answer "not found". */}
                        {isLibraryClip ? (
                            <Link
                                to={`/videos/${activeClip.videoRowId}`}
                                className="min-w-0 truncate text-xs text-primary hover:underline"
                            >
                                {activeClip.title}
                            </Link>
                        ) : (
                            <span className="min-w-0 truncate text-xs text-muted-foreground">
                                {activeClip.title}
                            </span>
                        )}
                    </p>

                    {isLibraryClip && (
                        <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="ml-auto h-8 rounded-full px-3 text-xs"
                        >
                            <Link to={`/videos/${activeClip.videoRowId}`}>
                                {t("pages.videos.inContext.openVideo", "Open full lesson")}
                            </Link>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
